import {
  NONCONFORMITY_SOURCE_LABEL_PT_BR,
  NONCONFORMITY_STATUS_LABEL_PT_BR,
  NONCONFORMITY_TYPE_LABEL_PT_BR,
  NonconformitySource,
  NonconformityStatus,
  NonconformityType,
  Permission,
  type NonconformityListFilter,
} from '@qualicloud/contracts';
import { CRITICALITY_LABEL_PT_BR, CriticalityLevel } from '@qualicloud/domain';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  DatePicker,
  EmptyState,
  Field,
  IconAlertOctagon,
  IconAlertTriangle,
  IconClipboardCheck,
  IconFilter,
  IconPlus,
  IconSearch,
  Input,
  Pagination,
  Popover,
  Select,
  Table,
  cn,
  focusRing,
  parseIso,
  type SortState,
  type TableColumn,
} from '@/design-system';
import { CriticalityBadge, NonconformityStatusBadge } from '@/components/sgq/sgq-badges';
import { nonconformitiesApi, queryKeys, toUserMessage } from '@/lib/api';
import type { NonconformityListRow } from '@/lib/api/sgq.types';
import { formatDateOnly } from '@/lib/format/datetime';
import { useDirectory } from '@/lib/use-directory';
import { useAuth } from '@/providers/auth-provider';
import type { NonconformitySearch } from '@/routes/search-schemas';
import { PageBody, PageHeader } from '@/shell/app-shell';

type SortKey = NonNullable<NonconformityListFilter['sortBy']>;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LISTA DE NÃO-CONFORMIDADES — sgq.md §A.8
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * O QUE FOI CORRIGIDO EM RELAÇÃO À GRADE DO LEGADO
 *
 * · DUPLICAÇÃO E SUMIÇO (BUG-NC-09): a consulta do legado fazia
 *   `JOIN erp_departamento_gerente` sem `DISTINCT`. Uma NC de setor com dois
 *   gerentes aparecia duas vezes; uma NC de setor SEM gerente cadastrado
 *   simplesmente não aparecia. A listagem nova não junta gerência.
 * · CRITICIDADE COM DUAS FAIXAS (BUG-NC-02): a grade classificava por
 *   `≤5 / 6-10 / >10` e o resto do sistema por `1-20 / 21-40 / ≥41`. A mesma NC
 *   tinha duas criticidades conforme a tela. Aqui a faixa vem do servidor,
 *   calculada com os limiares da organização, e a tela só desenha.
 * · ORIGEM: no legado o campo não existia — o `<select name="origemNC">` estava
 *   COMENTADO no formulário e a origem era inferida do tipo, um inteiro 1..25
 *   com três cópias divergentes do catálogo (BUG-NC-07). Origem e tipo são
 *   colunas próprias e filtráveis.
 * · PAGINAÇÃO E ORDENAÇÃO: existem, no servidor.
 *
 * ACESSIBILIDADE: a criticidade nunca é só cor — `<CriticalityBadge>` traz
 * ícone de forma distinta, o SCORE e a FAIXA por extenso.
 */
export function NonconformitiesPage() {
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const search = useSearch({ strict: false }) as unknown as NonconformitySearch;
  const directory = useDirectory();

  const filter: Partial<NonconformityListFilter> = useMemo(
    () => ({
      page: search.page ?? 1,
      pageSize: search.pageSize ?? 50,
      scope: (search.scope as NonconformityListFilter['scope']) ?? 'ALL',
      status: search.status as NonconformityStatus[] | undefined,
      type: search.type as NonconformityType[] | undefined,
      source: search.source as NonconformitySource[] | undefined,
      departmentId: search.departmentId,
      assigneeUserId: search.assigneeUserId,
      search: search.search,
      // A URL guarda `AAAA-MM-DD`; o filtro do contrato pede `Date`. A
      // conversão acontece aqui, num lugar só, em vez de mentir no tipo.
      detectedFrom: parseIso(search.detectedFrom ?? '') ?? undefined,
      detectedTo: parseIso(search.detectedTo ?? '') ?? undefined,
      onlyRecurrent: search.onlyRecurrent ?? false,
      onlyOverdueCapa: search.onlyOverdueCapa ?? false,
      sortBy: (search.sortBy as SortKey) ?? 'number',
      sortDir: search.sortDir ?? 'desc',
    }),
    [search],
  );

  /**
   * Recorte por FAIXA de criticidade.
   *
   * O filtro do servidor (`nonconformityListFilterSchema`) não tem faixa: ele
   * ordena por `criticality`, mas o corte em baixa/média/alta depende dos
   * limiares da organização, que são configuráveis. Filtrar aqui, sobre a
   * página carregada, é honesto desde que a tela DIGA que o recorte é da página
   * — e é o que o aviso abaixo da tabela faz. Inventar um parâmetro que o
   * servidor ignora seria pior: o usuário confiaria num filtro inexistente.
   */
  // Memoizado: é dependência do `useMemo` de `rows`, e `?? []` devolveria
  // um array novo a cada render, anulando a memoização da filtragem.
  const criticalityFilter = useMemo(
    () => (search.criticality ?? []) as CriticalityLevel[],
    [search.criticality],
  );

  const updateFilter = useCallback(
    (patch: Partial<NonconformitySearch>) => {
      void navigate({
        to: '/nao-conformidades',
        search: { ...search, ...patch, page: patch.page ?? 1 } as NonconformitySearch,
      });
    },
    [navigate, search],
  );

  const listQuery = useQuery({
    queryKey: queryKeys.nonconformities.list(filter),
    queryFn: () => nonconformitiesApi.list(filter),
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(() => {
    const items = listQuery.data?.items ?? [];
    if (criticalityFilter.length === 0) return items;
    return items.filter((row) => criticalityFilter.includes(row.criticalityLevel));
  }, [listQuery.data, criticalityFilter]);

  const columns = useMemo<Array<TableColumn<NonconformityListRow, SortKey>>>(
    () => [
      {
        id: 'number',
        header: 'Número',
        sortKey: 'number',
        width: '10%',
        cell: (row) => (
          <Link
            to="/nao-conformidades/$nonconformityId"
            params={{ nonconformityId: row.id }}
            className={cn('font-mono text-sm font-medium text-brand hover:underline', focusRing)}
          >
            {row.code}
          </Link>
        ),
      },
      {
        id: 'title',
        header: 'Título',
        width: '24%',
        cell: (row) => (
          <div className="min-w-0">
            <p className="truncate text-sm text-content">{row.title}</p>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              {row.recurrenceCount > 0 ? (
                <Badge tone="warning" size="sm">
                  Reincidente
                </Badge>
              ) : null}
              {row.capaOverdueCount > 0 ? (
                <Badge tone="danger" size="sm" icon={<IconAlertOctagon size={11} />}>
                  {row.capaOverdueCount === 1
                    ? '1 ação vencida'
                    : `${row.capaOverdueCount} ações vencidas`}
                </Badge>
              ) : null}
              {row.assigneeUserId === user?.id &&
              row.status !== NonconformityStatus.CLOSED &&
              row.status !== NonconformityStatus.NOT_APPLICABLE ? (
                <Badge tone="info" size="sm" icon={<IconClipboardCheck size={11} />}>
                  Sob sua responsabilidade
                </Badge>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        id: 'source',
        header: 'Origem',
        width: '12%',
        cell: (row) => (
          <span className="text-sm text-content-muted">
            {NONCONFORMITY_SOURCE_LABEL_PT_BR[row.source]}
          </span>
        ),
      },
      {
        id: 'type',
        header: 'Natureza',
        width: '13%',
        cell: (row) => (
          <span className="text-sm text-content-muted">
            {NONCONFORMITY_TYPE_LABEL_PT_BR[row.type]}
          </span>
        ),
      },
      {
        id: 'department',
        header: 'Departamento',
        width: '12%',
        cell: (row) => (
          <span className="text-sm text-content-muted">
            {directory.departments.find((item) => item.id === row.departmentId)?.name ?? '—'}
          </span>
        ),
      },
      {
        id: 'criticality',
        header: 'Criticidade GUT',
        sortKey: 'criticality',
        width: '11%',
        cell: (row) => (
          <CriticalityBadge
            score={row.managerCriticalityScore ?? row.criticalityScore}
            level={row.managerCriticalityLevel ?? row.criticalityLevel}
            origin={row.managerCriticalityScore === null ? 'reporter' : 'manager'}
          />
        ),
      },
      {
        id: 'status',
        header: 'Situação',
        sortKey: 'status',
        width: '10%',
        cell: (row) => <NonconformityStatusBadge status={row.status} />,
      },
      {
        id: 'detectedOn',
        header: 'Data do fato',
        sortKey: 'detectedOn',
        width: '8%',
        cell: (row) => (
          <span className="qc-tabular text-sm text-content-muted">
            {formatDateOnly(row.detectedOn)}
          </span>
        ),
      },
    ],
    [directory.departments, user?.id],
  );

  const activeFilters = countActiveFilters(search);

  return (
    <>
      <PageHeader
        title="Não-conformidades"
        description="Registros de não-conformidade, observação, oportunidade de melhoria, evento adverso e reclamação de cliente. A lista traz apenas os registros que você tem permissão para ver."
        actions={
          can(Permission.NC_CREATE) ? (
            <Button asChild variant="primary" leadingIcon={<IconPlus size={15} />}>
              <Link to="/nao-conformidades/nova">Abrir não-conformidade</Link>
            </Button>
          ) : null
        }
      />

      <PageBody>
        <Card padding="sm" className="mb-4">
          <FilterBar
            search={search}
            activeCount={activeFilters}
            departments={directory.departments}
            users={directory.subjects.map((subject) => ({
              id: subject.userId,
              name: subject.displayName,
            }))}
            onChange={updateFilter}
            onClear={() =>
              void navigate({ to: '/nao-conformidades', search: { page: 1 } as NonconformitySearch })
            }
          />
        </Card>

        {/*
          A falha da consulta precisa ser DITA. Sem este bloco a lista vazia por
          erro é indistinguível da lista vazia de verdade, e o leitor de tela não
          recebe nada — mesmo padrão de `document-admin.page.tsx`.
        */}
        {listQuery.isError ? (
          <p
            role="alert"
            className="mb-4 flex items-start gap-2 rounded-md border border-danger bg-danger-subtle px-3 py-2.5 text-sm text-danger-content"
          >
            <IconAlertTriangle size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
            <span>{toUserMessage(listQuery.error)}</span>
            <Button
              variant="secondary"
              size="sm"
              className="ml-auto shrink-0"
              onClick={() => void listQuery.refetch()}
            >
              Tentar de novo
            </Button>
          </p>
        ) : null}

        <Table<NonconformityListRow, SortKey>
          caption="Não-conformidades da organização. Colunas: número, título, origem, natureza, departamento, criticidade GUT, situação e data do fato."
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={listQuery.isPending}
          sort={{ by: (filter.sortBy ?? 'number') as SortKey, dir: filter.sortDir ?? 'desc' }}
          onSortChange={(sort: SortState<SortKey>) =>
            updateFilter({ sortBy: sort.by, sortDir: sort.dir })
          }
          empty={
            // Em erro, o aviso acima já diz o que houve. Passar `null` aqui cairia
            // no texto padrão da `<Table>` ("Nenhum registro encontrado"), que é a
            // mesma afirmação falsa que se quer evitar.
            listQuery.isError ? (
              <p className="px-4 py-10 text-center text-sm text-content-subtle">
                A lista não pôde ser carregada.
              </p>
            ) : (
              <EmptyState
                icon={<IconClipboardCheck size={22} />}
                title={
                  activeFilters > 0
                    ? 'Nenhuma não-conformidade corresponde aos filtros'
                    : 'Nenhuma não-conformidade registrada'
                }
                description={
                  activeFilters > 0
                    ? 'Ajuste ou limpe os filtros para ampliar a busca.'
                    : 'Quando um desvio for detectado, abra a não-conformidade aqui. O registro passa por avaliação de procedência, análise de causa, plano de ação e verificação de eficácia antes de poder ser encerrado.'
                }
                action={
                  activeFilters > 0 ? (
                    <Button
                      variant="secondary"
                      onClick={() =>
                        void navigate({
                          to: '/nao-conformidades',
                          search: { page: 1 } as NonconformitySearch,
                        })
                      }
                    >
                      Limpar filtros
                    </Button>
                  ) : can(Permission.NC_CREATE) ? (
                    <Button asChild variant="primary">
                      <Link to="/nao-conformidades/nova">Abrir a primeira não-conformidade</Link>
                    </Button>
                  ) : undefined
                }
              />
            )
          }
        />

        {criticalityFilter.length > 0 && listQuery.data ? (
          <p role="status" className="mt-2 text-xs text-content-muted">
            O recorte por faixa de criticidade é aplicado sobre a página atual ({rows.length} de{' '}
            {listQuery.data.items.length} registros nesta página). As demais páginas não são
            afetadas — para varrer todo o período, ordene por criticidade.
          </p>
        ) : null}

        {listQuery.data && listQuery.data.total > 0 ? (
          <div className="rounded-b-lg border-x border-b border-border bg-surface">
            <Pagination
              page={listQuery.data.page}
              pageSize={listQuery.data.pageSize}
              total={listQuery.data.total}
              itemNoun="não-conformidades"
              onPageChange={(page) => updateFilter({ page })}
              onPageSizeChange={(pageSize) => updateFilter({ pageSize, page: 1 })}
            />
          </div>
        ) : null}
      </PageBody>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Filtros
// ─────────────────────────────────────────────────────────────────────────────

function FilterBar({
  search,
  activeCount,
  departments,
  users,
  onChange,
  onClear,
}: {
  readonly search: NonconformitySearch;
  readonly activeCount: number;
  readonly departments: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  readonly users: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  readonly onChange: (patch: Partial<NonconformitySearch>) => void;
  readonly onClear: () => void;
}) {
  const [term, setTerm] = useState(search.search ?? '');

  return (
    <section aria-label="Filtros da lista de não-conformidades" className="flex flex-wrap items-end gap-3">
      <form
        role="search"
        className="min-w-[15rem] flex-1"
        onSubmit={(event) => {
          event.preventDefault();
          onChange({ search: term.trim() || undefined });
        }}
      >
        <label htmlFor="nc-search" className="qc-sr-only">
          Buscar por número ou trecho do título
        </label>
        <Input
          id="nc-search"
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Buscar por número ou título…"
          leadingIcon={<IconSearch size={15} />}
        />
      </form>

      <div className="w-52">
        <Field label="Situação">
          <Select
            value={search.status?.[0] ?? '__all__'}
            onValueChange={(value) =>
              onChange({ status: value === '__all__' ? undefined : [value] })
            }
            options={[
              { value: '__all__', label: 'Todas as situações' },
              ...Object.values(NonconformityStatus).map((status) => ({
                value: status,
                label: NONCONFORMITY_STATUS_LABEL_PT_BR[status],
              })),
            ]}
          />
        </Field>
      </div>

      <div className="w-52">
        <Field label="Escopo">
          <Select
            value={search.scope ?? 'ALL'}
            onValueChange={(value) => onChange({ scope: value })}
            options={[
              { value: 'ALL', label: 'Todas as que posso ver' },
              { value: 'ISSUED', label: 'Emitidas por mim' },
              {
                value: 'RECEIVED',
                label: 'Sob minha responsabilidade',
                description: 'Minhas pendências: NCs em que sou o responsável pelo tratamento.',
              },
            ]}
          />
        </Field>
      </div>

      <Popover
        width="22rem"
        title="Filtros avançados"
        showClose
        trigger={
          <Button variant="secondary" leadingIcon={<IconFilter size={15} />}>
            Mais filtros
            {activeCount > 0 ? (
              <Badge tone="brand" size="sm" className="ml-1">
                {activeCount}
              </Badge>
            ) : null}
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <Field label="Origem">
            <Select
              value={search.source?.[0] ?? '__all__'}
              onValueChange={(value) =>
                onChange({ source: value === '__all__' ? undefined : [value] })
              }
              options={[
                { value: '__all__', label: 'Todas as origens' },
                ...Object.values(NonconformitySource).map((source) => ({
                  value: source,
                  label: NONCONFORMITY_SOURCE_LABEL_PT_BR[source],
                })),
              ]}
            />
          </Field>

          <Field label="Natureza">
            <Select
              value={search.type?.[0] ?? '__all__'}
              onValueChange={(value) =>
                onChange({ type: value === '__all__' ? undefined : [value] })
              }
              options={[
                { value: '__all__', label: 'Todas as naturezas' },
                ...Object.values(NonconformityType).map((type) => ({
                  value: type,
                  label: NONCONFORMITY_TYPE_LABEL_PT_BR[type],
                })),
              ]}
            />
          </Field>

          <Field label="Departamento">
            <Select
              value={search.departmentId ?? '__all__'}
              onValueChange={(value) =>
                onChange({ departmentId: value === '__all__' ? undefined : value })
              }
              options={[
                { value: '__all__', label: 'Todos os departamentos' },
                ...departments.map((department) => ({
                  value: department.id,
                  label: department.name,
                })),
              ]}
            />
          </Field>

          <Field label="Responsável">
            <Select
              value={search.assigneeUserId ?? '__all__'}
              onValueChange={(value) =>
                onChange({ assigneeUserId: value === '__all__' ? undefined : value })
              }
              options={[
                { value: '__all__', label: 'Todos os responsáveis' },
                ...users.map((item) => ({ value: item.id, label: item.name })),
              ]}
            />
          </Field>

          <Field
            label="Faixa de criticidade GUT"
            description="O recorte por faixa vale apenas para a página exibida. A faixa depende dos limiares definidos pela organização."
          >
            <Select
              value={search.criticality?.[0] ?? '__all__'}
              onValueChange={(value) =>
                onChange({ criticality: value === '__all__' ? undefined : [value] })
              }
              options={[
                { value: '__all__', label: 'Todas as faixas' },
                ...Object.values(CriticalityLevel).map((level) => ({
                  value: level,
                  label: CRITICALITY_LABEL_PT_BR[level],
                })),
              ]}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Fato a partir de">
              <DatePicker
                value={search.detectedFrom ?? ''}
                onValueChange={(iso) => onChange({ detectedFrom: iso || undefined })}
              />
            </Field>
            <Field label="Fato até">
              <DatePicker
                value={search.detectedTo ?? ''}
                onValueChange={(iso) => onChange({ detectedTo: iso || undefined })}
              />
            </Field>
          </div>

          <Checkbox
            checked={search.onlyRecurrent ?? false}
            onCheckedChange={(checked) => onChange({ onlyRecurrent: checked || undefined })}
            label="Apenas reincidentes"
            description="NCs que declararam ao menos uma NC anterior como origem do mesmo problema."
          />

          <Checkbox
            checked={search.onlyOverdueCapa ?? false}
            onCheckedChange={(checked) => onChange({ onlyOverdueCapa: checked || undefined })}
            label="Com ação corretiva vencida"
            description="NCs cujo plano de ação tem ao menos uma ação fora do prazo."
          />

          {activeCount > 0 ? (
            <Button variant="ghost" onClick={onClear} className="self-start">
              Limpar todos os filtros
            </Button>
          ) : null}
        </div>
      </Popover>
    </section>
  );
}

function countActiveFilters(search: NonconformitySearch): number {
  let count = 0;
  if (search.status?.length) count += 1;
  if (search.type?.length) count += 1;
  if (search.source?.length) count += 1;
  if (search.departmentId) count += 1;
  if (search.assigneeUserId) count += 1;
  if (search.search) count += 1;
  if (search.detectedFrom) count += 1;
  if (search.detectedTo) count += 1;
  if (search.onlyRecurrent) count += 1;
  if (search.onlyOverdueCapa) count += 1;
  if (search.criticality?.length) count += 1;
  if (search.scope && search.scope !== 'ALL') count += 1;
  return count;
}
